/**
 * Public API endpoints (no auth required)
 */

import { fetchApi } from '../core';
import type {
  PublicCourseData,
  PublicCertificateResponse,
  PublicLogoUrlResponse,
  SaveAnswerData,
  StartPlacementTestData,
  StartTestResponse,
  PlacementTestInfo,
  SubmissionStatus,
} from '../types';

// Public Courses
export const courses = {
  getAll(componentName: string = 'Unknown') {
    return fetchApi<PublicCourseData[]>('/public/courses', {
      requireAuth: false
    }, componentName);
  }
};

// Public certificate verification (no auth; keyed by the opaque code)
export const certificates = {
  verify(code: string, componentName: string = 'Unknown') {
    return fetchApi<PublicCertificateResponse>(
      `/api/public/certificates/${encodeURIComponent(code)}`,
      { requireAuth: false },
      componentName
    );
  },

  // Institution logo for the verify page (no auth; 404 when there is no logo).
  logo(code: string, componentName: string = 'Unknown') {
    return fetchApi<PublicLogoUrlResponse>(
      `/api/public/certificates/${encodeURIComponent(code)}/logo`,
      { requireAuth: false },
      componentName
    );
  },
};

export interface SubmitAccessRequestData {
  first_name: string;
  last_name: string;
  email: string;
  message?: string;
}

// Public self-registration: a prospective student requests access to an
// institution identified by its slug. Gated server-side by the institution's
// allow_self_registration setting.
export interface RegistrationInfo {
  name: string;
  allow_self_registration: boolean;
}

export const accessRequests = {
  getInfo(slug: string, componentName: string = 'Unknown') {
    return fetchApi<RegistrationInfo>(
      `/api/public/tenants/${encodeURIComponent(slug)}/registration`,
      { requireAuth: false },
      componentName
    );
  },

  submit(slug: string, data: SubmitAccessRequestData, componentName: string = 'Unknown') {
    return fetchApi(`/api/public/tenants/${encodeURIComponent(slug)}/access-requests`, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: false,
    }, componentName);
  },
};

// Public OIDC SSO (no auth; the user has no session yet).
export interface SsoStartResponse {
  authorization_url: string;
}

export interface SsoCallbackResponse {
  token: string;
  user_id: string;
  email: string;
  name: string;
  tenant_id: string;
  jit_provisioned: boolean;
}

export const sso = {
  // Begin a login for the institution identified by `slug`; returns the IdP
  // authorization URL the browser should be sent to.
  start(slug: string, componentName: string = 'Unknown') {
    return fetchApi<SsoStartResponse>(
      `/api/public/sso/${encodeURIComponent(slug)}/start`,
      { method: 'POST', requireAuth: false },
      componentName
    );
  },

  // Complete a login: exchange the IdP's code+state for a session token.
  callback(data: { code: string; state: string }, componentName: string = 'Unknown') {
    return fetchApi<SsoCallbackResponse>(
      `/api/public/sso/callback`,
      { method: 'POST', body: JSON.stringify(data), requireAuth: false },
      componentName
    );
  },
};

// Public Placement Tests
export const placementTests = {
  getInfo(token: string, componentName: string = 'Unknown') {
    return fetchApi<PlacementTestInfo>(`/api/public/placement-tests/${encodeURIComponent(token)}/info`, {
      requireAuth: false
    }, componentName);
  },

  start(token: string, data: StartPlacementTestData, componentName: string = 'Unknown') {
    return fetchApi<StartTestResponse>(`/api/public/placement-tests/${encodeURIComponent(token)}/start`, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: false
    }, componentName);
  },

  saveAnswer(submissionId: string, data: SaveAnswerData, componentName: string = 'Unknown') {
    return fetchApi(`/api/public/submissions/${encodeURIComponent(submissionId)}/answer`, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: false
    }, componentName);
  },

  submit(
    submissionId: string,
    token: string,
    continuationToken: string,
    componentName: string = 'Unknown'
  ) {
    return fetchApi(`/api/public/submissions/${encodeURIComponent(submissionId)}/submit`, {
      method: 'POST',
      body: JSON.stringify({ token, continuation_token: continuationToken }),
      requireAuth: false
    }, componentName);
  },

  getStatus(
    submissionId: string,
    token: string,
    continuationToken: string,
    componentName: string = 'Unknown'
  ) {
    return fetchApi<SubmissionStatus>(`/api/public/submissions/${encodeURIComponent(submissionId)}/status`, {
      requireAuth: false,
      headers: {
        'X-Placement-Token': token,
        'X-Placement-Continuation-Token': continuationToken,
      },
    }, componentName);
  }
};
